import axios from "axios";
import { Accordion, AccordionTab } from "primereact/accordion";
import React, { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { useTranslation } from "react-i18next";
import { BiSolidCheckCircle, BiSolidMinusCircle } from "react-icons/bi";
import {
  MdAccessTimeFilled,
  MdChevronLeft,
  MdChevronRight,
  MdFlag,
  MdKeyboardArrowDown,
  MdKeyboardArrowUp,
} from "react-icons/md";

import useAuth from "../../../hooks/useAuth";
import { getLanguage } from "../../../utils/getLanguage";
import Button from "../../Button";
import ImageViewer from "../../ImageViewer";
import LoadingSpinner from "../../LoadingSpinner";
import Modal from "../../Modal";

const StudentQuestionsSolutionsModal = forwardRef(({}, ref) => {
  const { t } = useTranslation();
  const lang = getLanguage();
  const user = useAuth();
  const [selectedLessonID, setSelectedLessonID] = useState("");
  const [selectedStudentID, setSelectedStudentID] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [studentsData, setStudentsData] = useState(null);
  const [currentStudentIndex, setCurrentStudentIndex] = useState(0);
  const [studentName, setStudentName] = useState("");
  const [stats, setStats] = useState(null);
  const [lessonName, setLessonName] = useState("");
  const [activeTabIndex, setActiveTabIndex] = useState(null); // Track active tab index

  useImperativeHandle(ref, () => ({
    openModal(lessonName, lessonID, studentID, studentsData, index) {
      setSelectedLessonID(lessonID);
      setSelectedStudentID(studentID);
      setStudentsData(studentsData);
      setLessonName(lessonName);
      setCurrentStudentIndex(index);
      setStudentName(studentsData.find((student) => student.id === studentID)?.name || "");
      setIsOpen(true);
    },
    closeModal() {
      setIsOpen(false);
    },
  }));

  const handleCloseModal = () => {
    setIsOpen(false);
    setActiveTabIndex(null);
  };

  const fetchStudentQuestions = async (lessonID, studentID) => {
    setIsLoading(true);
    setError(null);

    try {
      const token = user?.access_token;

      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/student-questions/${lessonID}/${studentID}`,
        {
          headers: {
            "Content-Type": "application/json",
            lang,
            ...(token && { Authorization: `Bearer ${token}` }),
          },
        }
      );
      setQuestions(response?.data?.data);
      setStats(response?.data?.stats);
    } catch (error) {
      if (error.status == 400) {
        setError(t("student_not_solved_this_questions_after_reset"));
      } else {
        setError(t("failed_to_load_questions"));
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchStudentQuestions(selectedLessonID, selectedStudentID);
    }
  }, [isOpen, selectedStudentID]);

  useEffect(() => {
    if (studentsData) {
      const currentStudent = studentsData.find((student) => student.id === selectedStudentID);
      setStudentName(currentStudent?.name || "");
    }
  }, [selectedStudentID, studentsData]);

  const handleTabChange = (index) => {
    setActiveTabIndex((prevIndex) => (prevIndex === index ? null : index));
  };

  const goToNextStudent = () => {
    setActiveTabIndex(null);
    setCurrentStudentIndex((prevIndex) => {
      const nextIndex = prevIndex + 1;
      if (nextIndex < studentsData?.length) {
        setSelectedStudentID(studentsData[nextIndex].id);
        return nextIndex;
      }
      return prevIndex;
    });
  };

  const goToPreviousStudent = () => {
    setActiveTabIndex(null);
    setCurrentStudentIndex((prevIndex) => {
      const prevIndexValue = prevIndex - 1;
      if (prevIndexValue >= 0) {
        setSelectedStudentID(studentsData[prevIndexValue].id);
        return prevIndexValue;
      }
      return prevIndex;
    });
  };

  const renderOptions = (question, studentSubmission) => {
    // Get the student's selected answer(s)
    const studentAnswers = studentSubmission || [];

    return (
      <div
        className={`${question?.type.id === 2 ? "mt-3 grid grid-cols-2 gap-2" : "mt-3 flex flex-col gap-2"} `}
      >
        {question.options?.map((option, index) => {
          // Check if the option text matches the student's answer text
          const studentAnswer = studentAnswers.find((answer) => {
            if (question?.type.id === 2) {
              return answer.image === option.image;
            } else {
              return answer.text === option.text;
            }
          });
          const isStudentAnswer = studentAnswer !== undefined;
          const isCorrect = option.is_correct;

          return (
            <div key={index} className="flex items-center space-x-2">
              <input
                type="radio"
                name={`question-${question.id}`}
                value={option.text}
                checked={isStudentAnswer}
                // disabled
                className={`h-4 w-4 ${isCorrect ? "text-green-600" : isStudentAnswer ? "text-red-600" : "text-gray-800"}`}
              />
              <div
                className={`font-bold ${
                  isCorrect
                    ? "rounded-xl border-[#3DFF0A] p-3 ring-2 ring-[#3DFF0A]" // Highlight correct answers
                    : isStudentAnswer
                      ? "rounded-xl border-red-500 p-3 ring-2 ring-red-300" // Highlight incorrect answers
                      : "p-4 text-gray-800 ring-2 ring-transparent" // Default color for unselected answers
                }`}
              >
                {question?.type.id === 1 ? (
                  <div
                    className="py-4 text-xl font-medium text-gray-800"
                    dangerouslySetInnerHTML={{ __html: option.text }}
                  ></div>
                ) : (
                  <img
                    src={option.image}
                    alt={`Option ${option.id}`}
                    className="h-36 w-36 rounded-xl object-contain"
                  />
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <Modal
      isFullScreen
      isOpen={isOpen}
      onClose={handleCloseModal}
      header={
        <div className="text-center">
          <div className="rounded-lg border bg-gray-50 p-4 shadow-md dark:bg-gray-800">
            <p className="mb-3 mt-3 text-4xl font-extrabold text-blue-500">{studentName}</p>
            <div className="flex items-center justify-between space-x-8">
              <span className="rounded-lg text-blue-500 dark:bg-blue-900 dark:text-blue-300">
                {t("lesson_name")}: <strong>{lessonName}</strong>
              </span>
              {!error && (
                <>
                  <span className="text-blue-500 dark:bg-blue-900 dark:text-blue-300">
                    {t("questions_score")}:{" "}
                    <strong>
                      {stats?.correct_answers}/{stats?.total_questions}{" "}
                    </strong>
                  </span>
                  <span className="text-blue-500 dark:bg-blue-900 dark:text-blue-300">
                    {t("questions_rate")}: <strong>{stats?.question_rate}%</strong>
                  </span>
                  <span className="text-blue-500 dark:bg-blue-900 dark:text-blue-300">
                    {t("gained_skills")}:{" "}
                    <strong>
                      {stats?.gain_skills}/{stats?.total_skills}
                    </strong>
                  </span>
                  <span className="text-blue-500 dark:bg-blue-900 dark:text-blue-300">
                    {t("skills_rate")}: <strong>{Math.ceil(stats?.skill_rate)}%</strong>
                  </span>
                </>
              )}
            </div>
          </div>
        </div>
      }
      footer={
        <div className="flex justify-between">
          <Button
            iconLeft={<MdChevronLeft size={20} className={lang == "ar" && "rotate-180"} />}
            variant="text"
            onClick={goToPreviousStudent}
            disabled={currentStudentIndex === 0}
            label={t("previous")}
          />
          <Button
            iconRight={<MdChevronRight size={20} className={lang == "ar" && "rotate-180"} />}
            variant="text"
            onClick={goToNextStudent}
            disabled={currentStudentIndex === studentsData?.length - 1}
            label={t("next")}
          />
        </div>
      }
    >
      <div className="text-center text-gray-700">
        {isLoading ? (
          <LoadingSpinner />
        ) : error ? (
          <p className="text-red-500">{error}</p>
        ) : questions?.length === 0 ? (
          <p>{t("no_questions_available")}</p>
        ) : (
          questions?.length > 0 && (
            <Accordion>
              {questions.map((questionItem, index) => (
                <AccordionTab
                  key={index}
                  pt={{
                    headericon: "hidden",
                    headeraction: "bg-transparent p-0",
                    content: "p-0 pt-5",
                  }}
                  header={
                    <div
                      className={`flex items-center justify-between rounded-lg ${
                        activeTabIndex === index
                          ? "bg-blue-500 p-0 text-white"
                          : "bg-[#2B2D4B] p-0 text-white"
                      } p-4`}
                      onClick={() => handleTabChange(index)}
                    >
                      <span className="flex-1 text-start">
                        {t("skill")}: {questionItem?.skill?.name}
                      </span>
                      <span>
                        {t("correct_answers")}: {questionItem?.score?.percentage}%{" "}
                      </span>
                      <span className="mx-4 flex items-center space-x-2">
                        <MdFlag
                          size={24}
                          className={
                            questionItem?.score?.percentage >= 50 ? "text-green-500" : "text-red-500"
                          }
                        />

                        {activeTabIndex === index ? (
                          <MdKeyboardArrowUp size={24} className="text-white" />
                        ) : (
                          <MdKeyboardArrowDown size={24} className="text-white" />
                        )}
                      </span>
                    </div>
                  }
                >
                  {questionItem.questions.map((question, idx) => {
                    const submission = questionItem.student_submission?.[idx];
                    return (
                      <div
                        key={idx}
                        className="mb-6 rounded-md border bg-background p-4 shadow-lg dark:bg-dark-background"
                      >
                        <div className="flex items-center justify-between space-x-2">
                          <div className="flex items-center space-x-2">
                            {submission?.is_correct ? (
                              <BiSolidCheckCircle size={"24"} className="text-green-500" />
                            ) : (
                              <BiSolidMinusCircle size={"24"} className="text-red-500" />
                            )}
                            <p
                              className="text-start font-semibold text-gray-800"
                              dangerouslySetInnerHTML={{ __html: question?.text }}
                            ></p>
                          </div>
                          <div>
                            <div className="flex items-center space-x-2">
                              <MdAccessTimeFilled size={20} className="text-gray-500" />
                              <span>{submission?.submitted_date}</span>
                            </div>
                            {submission?.submitted_time}
                          </div>
                        </div>
                        {question?.image && <ImageViewer src={question.image} className="mb-4" />}

                        {renderOptions(question, questionItem.student_submission)}
                      </div>
                    );
                  })}
                </AccordionTab>
              ))}
            </Accordion>
          )
        )}
      </div>
    </Modal>
  );
});

export default StudentQuestionsSolutionsModal;
